import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";

// custom hooks
import useISPowner from "../../hooks/useISPOwner";

// internal import
import { handleActiveFilter } from "./activeFilter";
import { getOwnerUsers, userStaffs } from "../../features/getIspOwnerUsersApi";
import { getResellerUsers } from "../../features/apiCallReseller";
import useSelectorState from "../../hooks/useSelectorState";
import { fetchMikrotik, getAllPackages } from "../../features/apiCalls";

const DataFilter = ({
  page,
  filterData,
  setFilterData,
  filterOptions,
  setFilterOption,
  inputPermission,
  serverFiltering,
  filterFunction,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // get user & current user data form useISPOwner hooks
  const { role, ispOwnerId, bpSettings, hasMikrotik, userData, permission } =
    useISPowner();

  //---> Get redux store state data from useSelectorState hooks
  const {
    areas,
    subAreas,
    polesBox,
    mikrotiks,
    allPackages,
    resellers,
    bandwidthResellers,
  } = useSelectorState();

  // current month date
  let today = new Date();
  const { month: selectMonth } = filterOptions;
  const month = selectMonth ? selectMonth : today;

  // current start & end date
  var firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  var lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  var prevMonthDay = new Date(today.getFullYear(), today.getMonth() - 1);

  // current start & end date
  var filterFirstDayOfMonth = new Date(
    month?.getFullYear(),
    month?.getMonth(),
    1,
  );
  var filterLastDayOfMonth = new Date(
    month?.getFullYear(),
    month?.getMonth() + 1,
    0,
  );

  // current date and time
  firstDayOfMonth.setHours(0, 0, 0, 0);
  today.setHours(23, 59, 59, 999);

  // admin staff user role permission
  const adminUser =
    role === "ispOwner" ||
    role === "manager" ||
    (role === "collector" && !userData.reseller);

  const resellerUser =
    role === "reseller" || (role === "collector" && userData.reseller);

  // reseller id from role base
  const resellerId = role === "collector" ? userData.reseller : userData.id;

  // get mikrotik and without mikrotik packages in redux store
  const packages = useSelector((state) =>
    resellerUser ? state?.mikrotik?.pppoePackage : state?.package?.allPackages,
  );

  // get user staff data from redux store
  const staffs = useSelector((state) =>
    resellerUser
      ? state?.reseller?.resellerUsers
      : state?.ownerUsers?.userStaff,
  );

  const hotspotPackages = useSelector((state) => state.hotspot?.package);

  //---> Get ISP owner or reseller olt from redux store
  const olts = useSelector((state) => state?.olt.olt);

  // customers main data state
  const [mainData, setMainData] = useState([]);

  // mikrotik packages state
  const [mikrotikPackages, setMikrotikPackages] = useState([]);

  // loading state
  const [loading, setLoading] = useState(false);

  // api call
  useEffect(() => {
    setFilterOption({
      ...filterOptions,
      month: firstDayOfMonth,
    });

    // get user staffs api
    if (role === "ispOwner" || role === "manager") {
      staffs.length === 0 && userStaffs(dispatch);
    } else {
      staffs.length === 0 && getOwnerUsers(dispatch, ispOwnerId);
    }

    // get all mikrotik packages
    if (resellerUser) {
      //---> @Get reseller users data
      !staffs.length && getResellerUsers(dispatch, resellerId, setLoading);
    } else {
      //---> @Get ispOwner mikrotiks data
      !mikrotiks?.length && fetchMikrotik(dispatch, ispOwnerId, setLoading);

      //---> @Get ispOwner all mikrotik packages data
      !allPackages.length && getAllPackages(dispatch, ispOwnerId, setLoading);
    }
  }, []);

  useEffect(() => {
    if (mikrotiks?.length && page === "activeCustomer") {
      setFilterOption({
        ...filterOptions,
        mikrotik: mikrotiks[0]?.id,
      });

      const mtkPackage = packages.filter(
        (pack) => pack.mikrotik === mikrotiks[0]?.id,
      );
      setMikrotikPackages(mtkPackage);
    }
  }, [mikrotiks]);

  // set customers in state
  useEffect(() => {
    let modified = [];

    if (!serverFiltering) {
      filterData?.map((data) => {
        if (data.subArea) {
          subAreas?.map((sub) => {
            if (sub.id === data.subArea) {
              modified.push({
                ...data,
                area: sub.area,
                ...(data?.reseller && { reseller: data?.reseller?.id }),
              });
            }
          });
        } else if (data?.customer && data?.customer?.subArea) {
          subAreas?.map((sub) => {
            if (sub.id === data?.customer?.subArea) {
              modified.push({
                ...data,
                area: sub.area,
                subArea: sub.id,
                mikrotik: data?.customer?.mikrotik,
                mikrotikPackage: data?.customer?.mikrotikPackage?._id,
                userType: data?.customer.userType,
              });
            }
          });
        } else {
          modified.push({
            ...data,
          });
        }
      });
    }

    // set customers in state
    setMainData(modified);
  }, [filterData, subAreas]);

  // mikrotik handler method
  const mikrotikHandler = async (id) => {
    setFilterOption({
      ...filterOptions,
      mikrotik: id,
    });

    // if mikrotik id exist then filter packages or set empty
    if (id) {
      const mikrotikPackage = packages.filter((pack) => pack.mikrotik === id);
      setMikrotikPackages(mikrotikPackage);
    } else {
      setMikrotikPackages([]);
    }
  };

  // filter inputs options
  const filterInputs = [
    {
      name: "month",
      type: "date",
      id: "month",
      isVisible: inputPermission?.month ?? false,
      disabled: false,
      component: "DatePicker",
      dateFormat: "MMM-yyyy",
      showMonthYearPicker: true,
      showFullMonthYearPicker: true,
      minDate: new Date(userData?.createdAt),
      maxDate: ["dueCustomer"]?.includes(page) ? prevMonthDay : firstDayOfMonth,
      selected: filterOptions?.month,
      onChange: (date) => {
        setFilterOption({
          ...filterOptions,
          month: date,
        });
      },
    },
    {
      name: "startCreateDate",
      type: "date",
      id: "startCreateDate",
      isVisible: inputPermission?.startCreateDate ?? false,
      disabled: false,
      component: "DatePicker",
      dateFormat: "yyyy MM dd hh:mm a",
      timeIntervals: 60,
      showTimeSelect: "showTimeSelect",
      minDate: filterFirstDayOfMonth,
      maxDate:
        month?.getMonth() + 1 === today.getMonth() + 1
          ? today
          : filterLastDayOfMonth,
      selected: filterOptions?.startCreateDate,
      onChange: (date) => {
        setFilterOption({
          ...filterOptions,
          startCreateDate: date,
        });
      },
    },
    {
      name: "endCreateDate",
      type: "date",
      id: "endCreateDate",
      isVisible: inputPermission?.endCreateDate ?? false,
      disabled: false,
      component: "DatePicker",
      dateFormat: "yyyy MM dd hh:mm a",
      timeIntervals: 60,
      showTimeSelect: "showTimeSelect",
      minDate: filterFirstDayOfMonth,
      maxDate:
        month?.getMonth() + 1 === today.getMonth() + 1
          ? today
          : filterLastDayOfMonth,
      selected: filterOptions?.endCreateDate,
      onChange: (date) => {
        setFilterOption({
          ...filterOptions,
          endCreateDate: date,
        });
      },
    },
    {
      name: "mikrotik",
      type: "select",
      id: "mikrotik",
      isVisible:
        bpSettings?.hasMikrotik && (inputPermission?.mikrotik ?? false),
      disabled: false,
      options: mikrotiks,
      onChange: (e) => mikrotikHandler(e.target.value),
      value: filterOptions.mikrotik,
      firstOptions: t("mikrotik"),
      textAccessor: "name",
      valueAccessor: "id",
    },
    {
      name: "olt",
      type: "select",
      id: "olt",
      isVisible: inputPermission?.olt ?? false,
      disabled: false,
      options: olts,
      onChange: (e) => {
        setFilterOption({
          ...filterOptions,
          olt: e.target.value,
        });
      },
      value: filterOptions.olt,
      firstOptions: t("olt"),
      textAccessor: "name",
      valueAccessor: "id",
    },
    {
      name: "package",
      type: "select",
      id: "package",
      value: filterOptions.mikrotikPackage,
      isVisible: inputPermission?.package ?? false,
      disabled: false,
      onChange: (e) => {
        setFilterOption({
          ...filterOptions,
          mikrotikPackage: e.target.value,
        });
      },
      options: hasMikrotik
        ? packages.filter((pack) =>
            page === "pppoe"
              ? pack.packageType === "pppoe" &&
                pack.mikrotik === filterOptions.mikrotik
              : page === "static"
                ? pack.packageType === "queue" &&
                  pack.mikrotik === filterOptions.mikrotik
                : pack.mikrotik === filterOptions.mikrotik,
          )
        : packages,
      firstOptions: t("package"),
      textAccessor: "name",
      valueAccessor: "id",
    },
    {
      name: "hotspotPackage",
      type: "select",
      id: "hotspotPackage",
      value: filterOptions.hotspotPackage,
      isVisible: inputPermission?.hotspotPackage ?? false,
      disabled: false,
      onChange: (e) => {
        setFilterOption({
          ...filterOptions,
          hotspotPackage: e.target.value,
        });
      },
      options: hotspotPackages,
      firstOptions: t("package"),
      textAccessor: "name",
      valueAccessor: "id",
    },
    {
      name: "area",
      type: "select",
      id: "area",
      value: filterOptions.area,
      isVisible: inputPermission?.area ?? false,
      disabled: false,
      onChange: (e) => {
        setFilterOption({
          ...filterOptions,
          area: e.target.value,
        });
      },
      options: areas,
      firstOptions: t("allArea"),
      textAccessor: "name",
      valueAccessor: "id",
    },
    {
      name: "subArea",
      type: "select",
      id: "subArea",
      value: filterOptions.subArea,
      isVisible: inputPermission?.subArea ?? false,
      disabled: false,
      onChange: (e) => {
        setFilterOption({
          ...filterOptions,
          subArea: e.target.value,
        });
      },
      options: subAreas.filter((item) => item?.area === filterOptions.area),
      firstOptions: t("subArea"),
      textAccessor: "name",
      valueAccessor: "id",
    },
    {
      name: "poleBox",
      type: "select",
      id: "poleBox",
      value: filterOptions.poleBox,
      isVisible: bpSettings?.poleBox && (inputPermission?.poleBox ?? false),
      disabled: false,
      onChange: (e) => {
        setFilterOption({
          ...filterOptions,
          poleBox: e.target.value,
        });
      },
      options: polesBox.filter(
        (item) => item?.subArea === filterOptions.subArea,
      ),
      firstOptions: t("poleBox"),
      textAccessor: "name",
      valueAccessor: "id",
    },
    {
      name: "status",
      type: "select",
      id: "status",
      value: filterOptions.status,
      isVisible: inputPermission?.status ?? false,
      disabled: false,
      onChange: (e) => {
        setFilterOption({
          ...filterOptions,
          status: e.target.value,
        });
      },
      options: [
        {
          text: t("active"),
          value: "active",
        },
        {
          text: t("in active"),
          value: "inactive",
        },
        {
          text: t("expired"),
          value: "expired",
        },
      ],
      firstOptions: t("status"),
      textAccessor: "text",
      valueAccessor: "value",
    },
    {
      name: "paymentStatus",
      type: "select",
      id: "paymentStatus",
      value: filterOptions.paymentStatus,
      isVisible: inputPermission?.paymentStatus ?? false,
      disabled: false,
      onChange: (e) => {
        setFilterOption({
          ...filterOptions,
          paymentStatus: e.target.value,
        });
      },
      options: [
        {
          text: t("paid"),
          value: "paid",
        },
        {
          text: t("unpaid"),
          value: "unpaid",
        },
        {
          text: t("free"),
          value: "free",
        },
        {
          text: t("partialPayment"),
          value: "partial",
        },
        {
          text: t("advance"),
          value: "advance",
        },
        {
          text: t("overDue"),
          value: "overDue",
        },
      ],
      firstOptions: t("paymentStatus"),
      textAccessor: "text",
      valueAccessor: "value",
    },
    {
      name: "reseller",
      type: "select",
      id: "reseller",
      value: filterOptions.reseller,
      isVisible: inputPermission?.reseller ?? false,
      disabled: false,
      onChange: (e) =>
        setFilterOption({
          ...filterOptions,
          reseller: e.target.value,
        }),
      options: resellers,
      firstOptions: t("allReseller"),
      textAccessor: "name",
      valueAccessor: "id",
    },
    {
      name: "bandwidthReseller",
      type: "select",
      id: "bandwidthReseller",
      value: filterOptions.bandwidthReseller,
      isVisible: inputPermission?.bandwidthReseller ?? false,
      disabled: false,
      onChange: (e) =>
        setFilterOption({
          ...filterOptions,
          bandwidthReseller: e.target.value,
        }),
      options: bandwidthResellers,
      firstOptions: t("allReseller"),
      textAccessor: "name",
      valueAccessor: "_id",
    },
    {
      name: "createdBy",
      type: "select",
      id: "createdBy",
      value: filterOptions.createdBy,
      isVisible: inputPermission?.createdBy ?? false,
      disabled: false,
      onChange: (e) =>
        setFilterOption({
          ...filterOptions,
          createdBy: e.target.value,
        }),
      options: staffs,
      firstOptions: t("createdBy"),
      textAccessor: "name",
      valueAccessor: "user",
    },
    {
      name: "collectedBy",
      type: "select",
      id: "collectedBy",
      value: filterOptions.collectedBy,
      isVisible: inputPermission?.collectedBy ?? false,
      disabled: false,
      onChange: (e) =>
        setFilterOption({
          ...filterOptions,
          collectedBy: e.target.value,
        }),
      options: staffs,
      firstOptions: t("collectedBy"),
      textAccessor: "name",
      valueAccessor: "_id",
    },
    {
      name: "billType",
      type: "select",
      id: "billType",
      value: filterOptions.billType,
      isVisible: inputPermission?.billType ?? false,
      disabled: false,
      onChange: (e) =>
        setFilterOption({
          ...filterOptions,
          billType: e.target.value,
        }),
      options: [
        { value: "bill", text: t("monthBill") },
        { value: "connectionFee", text: t("connectionFee") },
      ],
      firstOptions: t("billType"),
      textAccessor: "text",
      valueAccessor: "value",
    },
    {
      name: "billingType",
      type: "select",
      id: "billingType",
      value: filterOptions.billingType,
      isVisible: inputPermission?.billingType ?? false,
      disabled: false,
      onChange: (e) =>
        setFilterOption({
          ...filterOptions,
          billingType: e.target.value,
        }),
      options: [
        { value: "monthly", text: t("monthly") },
        { value: "daily", text: t("daily") },
      ],
      firstOptions: t("billingType"),
      textAccessor: "text",
      valueAccessor: "value",
    },
    {
      name: "medium",
      type: "select",
      id: "medium",
      value: filterOptions.medium,
      isVisible: inputPermission?.medium ?? false,
      disabled: false,
      onChange: (e) =>
        setFilterOption({
          ...filterOptions,
          medium: e.target.value,
        }),
      options: [
        { value: "cash", text: t("handCash") },
        { value: "bKash", text: t("bKash") },
        { value: "rocket", text: t("rocket") },
        { value: "nagad", text: t("nagad") },

        { value: "bKashPG", text: t("bKashPG") },
        { value: "sslcommerz", text: t("sslCommerz") },
        { value: "uddoktapay", text: t("uddoktaPay") },

        { value: "IPN", text: t("instantPaymentNotification") },
        { value: "Webhook", text: t("webhook") },
        { value: "shurjoPay", text: t("shurjoPay") },
        { value: "others", text: t("others") },
      ],
      firstOptions: t("medium"),
      textAccessor: "text",
      valueAccessor: "value",
    },
    {
      name: "connectionStatus",
      type: "select",
      id: "connectionStatus",
      value: filterOptions.connectionStatus,
      isVisible: inputPermission?.connectionStatus ?? false,
      disabled: false,
      onChange: (e) =>
        setFilterOption({
          ...filterOptions,
          connectionStatus: e.target.value,
        }),
      options: [
        { value: "online", text: t("online") },
        { value: "offline", text: t("offline") },
      ],
      firstOptions: t("connectionStatus"),
      textAccessor: "text",
      valueAccessor: "value",
    },
    {
      name: "allCustomer",
      type: "select",
      id: "allCustomer",
      value: filterOptions.allCustomer,
      isVisible: inputPermission?.allCustomer ?? false,
      disabled: false,
      onChange: (e) =>
        setFilterOption({
          ...filterOptions,
          allCustomer: e.target.value,
        }),
      options: [
        { value: "freeUser", text: t("freeCustomer") },
        { value: "nonFreeUser", text: t("nonFreeCustomer") },
        { value: "prepaid", text: t("prepaid") },
        { value: "postpaid", text: t("postPaid") },
        { value: "newUser", text: t("newCustomer") },
      ],
      firstOptions: t("sokolCustomer"),
      textAccessor: "text",
      valueAccessor: "value",
    },
    {
      name: "billDayLeft",
      type: "select",
      id: "billDayLeft",
      value: filterOptions.billDayLeft,
      isVisible: inputPermission?.billDayLeft ?? false,
      disabled: false,
      onChange: (e) =>
        setFilterOption({
          ...filterOptions,
          billDayLeft: e.target.value,
        }),
      options: [
        { value: "1", text: t("oneDayLeft") },
        { value: "2", text: t("twoDayLeft") },
        { value: "3", text: t("threeDayLeft") },
        { value: "4", text: t("fourDayLeft") },
        { value: "7", text: t("sevenDayLeft") },
      ],
      firstOptions: t("filterBillDate"),
      textAccessor: "text",
      valueAccessor: "value",
    },
    {
      name: "startDate",
      type: "date",
      id: "startDate",
      isVisible: inputPermission?.startDate ?? false,
      disabled: false,
      placeholderText: t("startBillingCycleDate"),
      component: "DatePicker",
      dateFormat: "yyyy MM dd hh:mm a",
      timeIntervals: 30,
      showTimeSelect: "showTimeSelect",
      selected: filterOptions.startDate,
      onChange: (date) => {
        setFilterOption({
          ...filterOptions,
          startDate: date,
        });
      },
    },
    {
      name: "endDate",
      type: "date",
      id: "endDate",
      isVisible: inputPermission?.endDate ?? false,
      disabled: false,
      placeholderText: t("endBillingCycleDate"),
      component: "DatePicker",
      dateFormat: "yyyy MM dd hh:mm a",
      timeIntervals: 30,
      showTimeSelect: "showTimeSelect",
      selected: filterOptions.endDate,
      onChange: (date) => {
        setFilterOption({
          ...filterOptions,
          endDate: date,
        });
      },
    },
    {
      name: "startPromiseDate",
      type: "date",
      id: "startPromiseDate",
      isVisible:
        (adminUser || permission?.promiseDate) &&
        (inputPermission?.startPromiseDate ?? false),
      disabled: false,
      placeholderText: t("startPromiseDate"),
      component: "DatePicker",
      dateFormat: "yyyy MM dd hh:mm a",
      timeIntervals: 30,
      showTimeSelect: "showTimeSelect",
      selected: filterOptions.startPromiseDate,
      onChange: (date) => {
        setFilterOption({
          ...filterOptions,
          startPromiseDate: date,
        });
      },
    },
    {
      name: "endPromiseDate",
      type: "date",
      id: "endPromiseDate",
      isVisible:
        (adminUser || permission?.promiseDate) &&
        (inputPermission?.startPromiseDate ?? false),
      disabled: false,
      placeholderText: t("endPromiseDate"),
      component: "DatePicker",
      dateFormat: "yyyy MM dd hh:mm a",
      timeIntervals: 30,
      showTimeSelect: "showTimeSelect",
      selected: filterOptions.endPromiseDate,
      onChange: (date) => {
        setFilterOption({
          ...filterOptions,
          endPromiseDate: date,
        });
      },
    },
    {
      name: "changeCustomer",
      type: "select",
      id: "changeCustomer",
      value: filterOptions.changeCustomer,
      isVisible: inputPermission?.changeCustomer ?? false,
      disabled: false,
      onChange: (e) => {
        setFilterOption({
          ...filterOptions,
          changeCustomer: e.target.value,
        });
      },
      options: [
        {
          text: t("promiseDate"),
          value: "promiseDate",
        },
        {
          text: t("connectionOn"),
          value: "true",
        },
        {
          text: t("connectionOff"),
          value: "false",
        },
      ],
      firstOptions: t("changeCustomer"),
      textAccessor: "text",
      valueAccessor: "value",
    },
    {
      name: "userType",
      type: "select",
      id: "userType",
      value: filterOptions.userType,
      isVisible:
        inputPermission?.userType && bpSettings?.customerType?.length > 1
          ? true
          : false,
      disabled: false,
      onChange: (e) => {
        setFilterOption({
          ...filterOptions,
          userType: e.target.value,
        });
      },
      options: [
        ...(bpSettings?.customerType.includes("pppoe")
          ? [{ text: t("pppoe"), value: "pppoe" }]
          : []),
        ...(bpSettings?.customerType.includes("static")
          ? [{ text: t("static"), value: "static" }]
          : []),
        ...(bpSettings?.customerType.includes("hotspot")
          ? [{ text: t("hotspot"), value: "hotspot" }]
          : []),
      ],

      firstOptions: t("userType"),
      textAccessor: "text",
      valueAccessor: "value",
    },
  ];

  // filter reset controller
  const handleFilterReset = () => {
    if (Object.keys(filterOptions).length > 0) {
      const resetOptions = Object.fromEntries(
        filterInputs
          .filter((input) => input.isVisible)
          .map((input) => {
            const { name } = input;

            if (name === "month") {
              const oldMonth = new Date(filterOptions[name]).getMonth();
              const currentMonth = new Date(today).getMonth();

              if (oldMonth === currentMonth) {
                return [name, filterOptions[name]];
              }

              return [name, page === "dueCustomer" ? prevMonthDay : today];
            }

            if (name === "startCreateDate") {
              const month = new Date(filterOptions?.month || today);
              const createStart = new Date(
                month.getFullYear(),
                month.getMonth(),
                1,
              );

              return [name, createStart];
            }

            if (name === "endCreateDate") {
              return [name, today];
            }

            return [name, ""];
          }),
      );

      // set empty mikrotik packages
      setMikrotikPackages([]);

      // set empty customers
      setFilterData(filterData);

      if (serverFiltering) {
        filterFunction(resetOptions);
      }

      setFilterOption(resetOptions);
    }
  };

  return (
    <>
      <div className="displayGrid6">
        {filterInputs.map((item) => {
          if (item.isVisible) {
            return item.type === "select" ? (
              <select
                className="form-select shadow-none mt-0"
                onChange={item.onChange}
                value={item.value}
              >
                <option value="">{item.firstOptions}</option>
                {item.options?.map((opt) => (
                  <option value={opt[item.valueAccessor]}>
                    {opt[item.textAccessor]}
                  </option>
                ))}
              </select>
            ) : (
              <div>
                <DatePicker className="form-control mt-0" {...item} />
              </div>
            );
          }
        })}

        <div className="displayGrid1">
          <button
            className="btn btn-outline-primary"
            type="button"
            onClick={() =>
              serverFiltering
                ? filterFunction()
                : setFilterData(handleActiveFilter(mainData, filterOptions))
            }
          >
            {t("filter")}
          </button>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={handleFilterReset}
          >
            {t("reset")}
          </button>
        </div>
      </div>
    </>
  );
};

export default DataFilter;
